import { RunwayData, RunwayWindAnalysis } from './types';

const CYRC_MAIN_RUNWAY_END = '30';
const CYRC_MAIN_RUNWAY_MAX_CROSSWIND_KT = 5;
const CYRC_MAX_TAILWIND_FOR_MAIN_RUNWAY_KT = 0;

export function parseWindFromMetar(metar: string): {
  direction: number;
  speed: number;
  gusts: number;
} {
  const windRegex = /(\d{3}|VRB)(\d{2,3})(G(\d{2,3}))?KT/;
  const match = metar.match(windRegex);
  if (!match) return { direction: 0, speed: 0, gusts: 0 };
  const direction = match[1] === 'VRB' ? 0 : parseInt(match[1], 10);
  const speed = parseInt(match[2], 10);
  const gusts = match[4] ? parseInt(match[4], 10) : 0;
  return { direction, speed, gusts };
}

export function calculateHeadwind(
  windDir: number,
  windSpeed: number,
  runwayHeading: number
): number {
  const angle = ((windDir - runwayHeading + 360) % 360) * (Math.PI / 180);
  return Math.round(windSpeed * Math.cos(angle));
}

export function calculateCrosswind(
  windDir: number,
  windSpeed: number,
  runwayHeading: number
): number {
  const angle = ((windDir - runwayHeading + 360) % 360) * (Math.PI / 180);
  return Math.round(Math.abs(windSpeed * Math.sin(angle)));
}

export function selectRecommendedRunway(
  windDir: number,
  windSpeed: number,
  runways: RunwayData[]
): string {
  const runway1230 = runways.find((r) => r.name === '12' || r.oppositeName === '30');

  if (runway1230) {
    const runway30Heading =
      runway1230.name === CYRC_MAIN_RUNWAY_END
        ? runway1230.heading
        : runway1230.oppositeHeading;

    const headwind30 = calculateHeadwind(windDir, windSpeed, runway30Heading);
    const crosswind30 = calculateCrosswind(windDir, windSpeed, runway30Heading);
    const tailwind30 = headwind30 < 0 ? Math.abs(headwind30) : 0;

    if (
      crosswind30 <= CYRC_MAIN_RUNWAY_MAX_CROSSWIND_KT &&
      tailwind30 <= CYRC_MAX_TAILWIND_FOR_MAIN_RUNWAY_KT
    ) {
      return CYRC_MAIN_RUNWAY_END;
    }
  }

  // Fallback: best score based on headwind/crosswind
  let bestRunway = '';
  let bestScore = -Infinity;

  for (const runway of runways) {
    const ends = [
      { name: runway.name, heading: runway.heading },
      { name: runway.oppositeName, heading: runway.oppositeHeading },
    ];
    for (const end of ends) {
      const headwind = calculateHeadwind(windDir, windSpeed, end.heading);
      const crosswind = calculateCrosswind(windDir, windSpeed, end.heading);
      const tailwindPenalty = headwind < 0 ? Math.abs(headwind) * 3 : 0;
      const score = headwind - crosswind * 0.6 - tailwindPenalty;
      if (score > bestScore) {
        bestScore = score;
        bestRunway = end.name;
      }
    }
  }

  return bestRunway;
}

export function analyzeRunwayWind(
  windDir: number,
  windSpeed: number,
  runway: RunwayData
): RunwayWindAnalysis {
  const headwindA = calculateHeadwind(windDir, windSpeed, runway.heading);
  const crosswindA = calculateCrosswind(windDir, windSpeed, runway.heading);
  const headwindB = calculateHeadwind(windDir, windSpeed, runway.oppositeHeading);
  const crosswindB = calculateCrosswind(windDir, windSpeed, runway.oppositeHeading);

  return {
    runwayId: runway.id,
    runwayName: `${runway.name}/${runway.oppositeName}`,
    components: {
      headwind: headwindA > 0 ? headwindA : 0,
      tailwind: headwindA < 0 ? Math.abs(headwindA) : 0,
      crosswind: crosswindA,
      isRecommended: false,
      runwayEnd: runway.name,
    },
    oppositeComponents: {
      headwind: headwindB > 0 ? headwindB : 0,
      tailwind: headwindB < 0 ? Math.abs(headwindB) : 0,
      crosswind: crosswindB,
      isRecommended: false,
      runwayEnd: runway.oppositeName,
    },
  };
}

export function getAllRunwayAnalyses(
  windDir: number,
  windSpeed: number,
  runways: RunwayData[]
): RunwayWindAnalysis[] {
  const recommended = selectRecommendedRunway(windDir, windSpeed, runways);
  const isMainRunwayRule = recommended === CYRC_MAIN_RUNWAY_END;

  return runways.map((runway) => {
    const analysis = analyzeRunwayWind(windDir, windSpeed, runway);
    analysis.components.isRecommended = analysis.components.runwayEnd === recommended;
    analysis.oppositeComponents.isRecommended = analysis.oppositeComponents.runwayEnd === recommended;

    if (analysis.components.isRecommended) {
      analysis.components.selectionReason = isMainRunwayRule
        ? 'Piste principale généralement en service si le traversier est faible.' :'Meilleure composante de vent selon le METAR.';
    }

    if (analysis.oppositeComponents.isRecommended) {
      analysis.oppositeComponents.selectionReason = isMainRunwayRule
        ? 'Piste principale généralement en service si le traversier est faible.' :'Meilleure composante de vent selon le METAR.';
    }

    return analysis;
  });
}